// modules
import { useSelector, useDispatch } from 'react-redux';
import { useState } from 'react';
import { styled, Box, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// local imports
import { RootState, uiActions } from '../../store';
import { PrimaryButton } from '../../App/SidePanel/PrimaryButton';
import CustomDialog from '../../App/CustomDialog';

const CustomTooltip = styled(Tooltip)(() => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
}));

const TransparentBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.text.primary}`,
  backgroundColor: 'transparent',
  padding: theme.spacing(1.5),
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  maxWidth: '365px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

/**
 * Share
 *
 * Renders a modal with a shareable link with the current state of the application.
 *
 * @returns {JSX.Element} The rendered Share component.
 */
export default function ShareDialog() {
  const { shareDialogOpen } = useSelector((state: RootState) => state.ui);
  const shareUrl = window.location.href;
  const [showCopiedNotification, setShowCopiedNotification] = useState(false);

  const dispatch = useDispatch();
  const handleClose = () => {
    dispatch(uiActions.toggleShareDialog());
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShowCopiedNotification(true);
      // Hide the notification after 2 seconds
      setTimeout(() => {
        setShowCopiedNotification(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <CustomDialog
      open={shareDialogOpen}
      onClose={handleClose}
      title="Share"
      dataTestId="share-dialog"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TransparentBox aria-hidden="true">{shareUrl}</TransparentBox>
        <CustomTooltip
          title="COPIED"
          placement="top"
          open={showCopiedNotification}
          disableHoverListener
          disableFocusListener
          disableTouchListener
        >
          <PrimaryButton
            aria-label="Copy sharable link"
            variant="contained"
            onClick={handleCopy}
            startIcon={<ContentCopyIcon />}
            sx={{
              marginBottom: '1rem',
            }}
          >
            Copy
          </PrimaryButton>
        </CustomTooltip>
      </Box>
    </CustomDialog>
  );
}
